import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormGroup, FormControl } from '@angular/forms'
import { Store } from '@ngrx/store'
import { Actions, ofType } from '@ngrx/effects'
import { filter } from 'rxjs/operators'

import { ScreenConfigApiModel, ScreenConfigValueApiModel } from 'src/app/models/api.model'

import { MatStepper } from '@angular/material/stepper'
import Tooltip from 'bootstrap/js/dist/tooltip'
import { FieldBlueprint, RateOfContributionValues } from 'src/app/constants/config-blueprint'
import { selectEmployeeConfig, selectAdminConfig, selectForm11Config } from 'src/app/store/selector/onboarding.selector'
import {
  loadScreenConfigByType,
  saveScreenConfig,
  saveScreenConfigSuccess
} from 'src/app/store/action/onboarding.action'

@Component({
  selector: 'app-shared-wf-screen-config',
  templateUrl: './shared-wf-screen-config.component.html',
  styleUrls: ['./shared-wf-screen-config.component.scss']
})
export class SharedWfScreenConfigComponent implements OnInit {
  @Input() SCREEN_TYPE!: string
  @Input() steps!: { title: string; blueprint: FieldBlueprint[] }[]
  @Input() massAction: boolean = true
  @Output() completed = new EventEmitter<void>()

  screenTitle = this.SCREEN_TYPE?.replace(/_/g, ' ')

  activeStep = 0
  form = new FormGroup({})
  loadedConfig: ScreenConfigApiModel | null = null

  selectedFields = new Set<string>()
  selectAllChecked = false
  bulkOptions: { label: string; value: string }[] = []
  showBulkDropdown = false

  VH = [
    { label: 'Hidden', value: 'HIDDEN' },
    { label: 'Visible', value: 'VISIBLE' }
  ]

  MO = [
    { label: 'Mandatory', value: 'MANDATORY' },
    { label: 'Optional', value: 'OPTIONAL' }
  ]

  HMO = [
    { label: 'Hidden', value: 'HIDDEN' },
    { label: 'Mandatory', value: 'MANDATORY' },
    { label: 'Optional', value: 'OPTIONAL' }
  ]

  STRIKE = [
    { label: 'Yes', value: 'YES' },
    { label: 'No', value: 'NO' }
  ]

  RATE = [
    { label: '12% Gross', value: RateOfContributionValues.RATE_1 },
    { label: '12% PF Wage', value: RateOfContributionValues.RATE_2 },
    { label: 'Others', value: RateOfContributionValues.RATE_3 }
  ]

  animationState: 'left' | 'center' | 'right' = 'center'

  constructor(private store: Store, private actions$: Actions) {}

  ngOnInit(): void {
    if (!this.SCREEN_TYPE || !this.steps) {
      throw new Error('SharedWfScreenConfigComponent requires SCREEN_TYPE and steps @Input()')
    }

    this.buildFormForStep(this.activeStep)
    const selector = this.getSelectorForType(this.SCREEN_TYPE)

    if (selector) {
      this.store
        .select(selector)
        .pipe(filter(cfg => cfg !== null && cfg !== undefined))
        .subscribe(cfg => {
          this.loadedConfig = cfg!
          this.populateForm(cfg!)
        })
    }

    this.store.dispatch(loadScreenConfigByType({ screenType: this.SCREEN_TYPE }))

    this.actions$
      .pipe(
        ofType(saveScreenConfigSuccess),
        filter(({ config }) => config.type === this.SCREEN_TYPE)
      )
      .subscribe(({ config }) => {
        this.loadedConfig = config

        const isLastStep = this.activeStep === this.steps.length - 1

        if (isLastStep) {
          this.completed.emit()
          return
        }

        this.animate('left')
        setTimeout(() => {
          this.activeStep++
          if (this.stepperRef) this.stepperRef.selectedIndex = this.activeStep
          this.clearSelection()
          this.buildFormForStep(this.activeStep)
          this.populateForm(config)
          this.animate('center')
        }, 100)
      })
  }

  private stepperRef: MatStepper | null = null
  registerStepper(stepper: MatStepper) {
    this.stepperRef = stepper
  }

  buildFormForStep(step: number) {
    this.form = new FormGroup({})
    this.steps[step].blueprint.forEach(f => this.form.addControl(f.fieldId, new FormControl('')))
  }

  populateForm(cfg: ScreenConfigApiModel) {
    if (!cfg?.values) return
    const map = Object.fromEntries(cfg.values.map(v => [v.fieldId, v.fieldValue]))

    this.steps[this.activeStep].blueprint.forEach(f => {
      if (this.form.get(f.fieldId)) {
        this.form.get(f.fieldId)!.setValue(map[f.fieldId] ?? '')
      }
    })
  }

  toggleSelection(fieldId: string) {
    if (this.selectedFields.has(fieldId)) this.selectedFields.delete(fieldId)
    else this.selectedFields.add(fieldId)
    this.updateSelectAllState()
    this.evaluateBulkVisibility()
  }

  evaluateBulkVisibility() {
    if (this.selectedFields.size === 0) {
      this.showBulkDropdown = false
      this.bulkOptions = []
      return
    }

    const currentBp = this.steps[this.activeStep].blueprint
    const selectedBps = currentBp.filter(b => this.selectedFields.has(b.fieldId))
    const types = Array.from(new Set(selectedBps.map(b => b.configurationType)))

    if (types.length === 1 && types[0] !== 'FreeText') {
      const t = types[0]

      switch (t) {
        case 'HiddenMandatoryOptional':
          this.bulkOptions = this.HMO
          break
        case 'StrikeOff':
          this.bulkOptions = this.STRIKE
          break
        case 'RateOfContribution':
          this.bulkOptions = this.RATE
          break
      }

      this.showBulkDropdown = true
    } else {
      this.showBulkDropdown = false
      this.bulkOptions = []
    }
  }

  applyBulk(value: string) {
    this.selectedFields.forEach(fid => {
      if (this.form.get(fid)) {
        this.form.get(fid)!.setValue(value)
      }
    })
  }

  clearSelection() {
    this.selectedFields.clear()
    this.selectAllChecked = false
    this.evaluateBulkVisibility()
  }

  getOptions(f: FieldBlueprint) {
    if (f.options) {
      return f.options
    }

    switch (f.configurationType) {
      case 'HiddenMandatoryOptional':
        return this.HMO
      case 'StrikeOff':
        return this.STRIKE
      case 'RateOfContribution':
        return this.RATE
      default:
        return []
    }
  }

  isFreeText(f: FieldBlueprint) {
    return f.configurationType === 'FreeText'
  }

  onProceed() {
    if (!this.loadedConfig) return

    const allFieldIds = this.steps.flatMap(s => s.blueprint.map(b => b.fieldId))

    const currentMap: Record<string, string> = {}
    Object.keys(this.form.controls).forEach(k => {
      currentMap[k] = this.form.get(k)!.value ?? ''
    })

    const loadedMap: Record<string, string> = {}
    this.loadedConfig.values.forEach(v => (loadedMap[v.fieldId] = v.fieldValue))

    const values: ScreenConfigValueApiModel[] = allFieldIds.map(fid => ({
      id: this.loadedConfig!.values.find(v => v.fieldId === fid)?.id,
      fieldId: fid,
      fieldValue: currentMap[fid] ?? loadedMap[fid] ?? ''
    }))

    const payload: Partial<ScreenConfigApiModel> = {
      id: this.loadedConfig.id,
      description: this.loadedConfig.description,
      type: this.loadedConfig.type,
      companyGroupId: this.loadedConfig.companyGroupId,
      values
    }

    this.store.dispatch(saveScreenConfig({ config: payload }))
  }

  saveAndNext(stepper: MatStepper) {
    this.registerStepper(stepper)
    this.onProceed()
  }

  goBack(stepper: MatStepper) {
    if (this.activeStep > 0) {
      this.activeStep--
      stepper.selectedIndex = this.activeStep

      setTimeout(() => {
        this.buildFormForStep(this.activeStep)
        if (this.loadedConfig) this.populateForm(this.loadedConfig)
        this.clearSelection()
      })
    }
  }

  animate(direction: 'left' | 'right' | 'center') {
    this.animationState = direction
  }

  onToggleSelectAll(event: any) {
    const checked = event.target.checked
    this.selectAllChecked = checked

    const currentBlueprint = this.steps[this.activeStep].blueprint
    currentBlueprint.forEach(f => {
      if (f.configurationType !== 'FreeText') {
        if (checked) this.selectedFields.add(f.fieldId)
        else this.selectedFields.delete(f.fieldId)
      }
    })

    this.evaluateBulkVisibility()
  }

  updateSelectAllState() {
    const currentBlueprint = this.steps[this.activeStep].blueprint
    const selectable = currentBlueprint.filter(f => f.configurationType !== 'FreeText').map(f => f.fieldId)

    this.selectAllChecked = selectable.length > 0 && selectable.every(id => this.selectedFields.has(id))
  }

  private getSelectorForType(screenType: string) {
    switch (screenType) {
      case 'EMPLOYEE_NEW_JOINEE_WORKFLOW_CONFIG':
        return selectEmployeeConfig
      case 'ADMIN_NEW_JOINEE_WORKFLOW_CONFIG':
        return selectAdminConfig
      case 'FORM_11_PDF_CONFIG':
        return selectForm11Config
      default:
        return null
    }
  }

  onStepChange(event: any) {
    setTimeout(() => {
      this.activeStep = event.selectedIndex
      this.buildFormForStep(this.activeStep)
      if (this.loadedConfig) this.populateForm(this.loadedConfig)
      this.clearSelection()
    })
  }

  ngAfterViewInit() {
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
    tooltipTriggerList.map((el: any) => new Tooltip(el))
  }
}
